'use client';

import { Fragment, ReactNode, SyntheticEvent, useContext } from 'react';
import { Controller, FieldError, get } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { PropsWithClasses } from '@lamesarv-sdk/types';

import { CommonFormContext } from '.';
import { getFacetCode } from '@lamesarv-sdk/tools';

interface ICommonOptionsProps extends PropsWithClasses {
  name: string;
  optionsList: { label: ReactNode | string; value: string }[];
  label?: string;
  hideLabel?: boolean;
  isRequired?: boolean;
  classContainer?: string;
  classContainerLabel?: string;
  classLabel?: string;
}

export const CommonOptions = (props: ICommonOptionsProps) => {
  const { isDisabled } = useContext(CommonFormContext);

  const getOptionsValue = (currentValues: string, optionValue: string, checked: boolean) => {
    const values = currentValues ? currentValues.split('\n') : [];

    if (checked) {
      if (!values.includes(optionValue)) {
        values.push(optionValue);
      }
    } else {
      const index = values.indexOf(optionValue);
      if (index > -1) {
        values.splice(index, 1);
      }
    }

    return values.join('\n');
  };

  return (
    <div className={twMerge('flex flex-col gap-2 text-left', props.classContainer)}>
      {!props.hideLabel && (
        <div className={props.classContainerLabel}>
          {props.label}
          {props.isRequired && <span className="ml-1 text-red-700 leading-none">*</span>}
        </div>
      )}
      <Controller
        name={props.name}
        defaultValue=""
        render={({ field, formState: { errors } }) => {
          const error = get(errors, props.name) as FieldError | null;
          return (
            <Fragment>
              <input type="hidden" name={field.name} value={field.value} />
              {props.optionsList.map((option) => (
                <div
                  key={getFacetCode(option.value)}
                  className={twMerge('flex flex-row items-center gap-2 group', props.className)}
                >
                  <input
                    type="checkbox"
                    value={option.value}
                    className={twMerge(
                      'border border-black-350 size-3 disabled:opacity-60',
                      props.classInput,
                      error && 'border-red-700',
                    )}
                    disabled={isDisabled}
                    onChange={(e: SyntheticEvent) => {
                      const optionsValue = getOptionsValue(
                        field.value,
                        option.value,
                        (e.target as HTMLInputElement).checked,
                      );

                      field.onChange(optionsValue);
                    }}
                  />
                  <span className={props.classLabel}>{option.label}</span>
                </div>
              ))}
              {error && <span className="text-left text-red-700 text-sm">{error.message}</span>}
            </Fragment>
          );
        }}
      />
    </div>
  );
};
